/**
 * schema.org Event JSON-LD, one per event and shown date (spec §4.5, K-5).
 * Every URL is the event's share view https://kgob.com/events?e={slug}; never a join link, never app.kgob.com.
 */
import { effectiveAvailability, sessionState } from "./availability";
import { isoWithOffset } from "./time";

export const SITE_ORIGIN = "https://kgob.com";
export const KGOB_ORGANIZER = { name: "KGOB CPAs & Advisors", url: "https://kgob.com" };

/** A shared link points at the event's own page now that kgob.com owns /events/{slug} (WHO-OWNS-WHICH-URL.md §3).
 *  `?e={slug}` still resolves: the landing replaces it with this URL, because old share cards are in the wild. */
export const shareUrl = (siteOrigin, slug) => `${siteOrigin}/events/${encodeURIComponent(slug)}`;

export function eventJsonLd(event, s, siteOrigin = SITE_ORIGIN, now = new Date()) {
  const pageUrl = shareUrl(siteOrigin, event.slug);
  const state = sessionState(s, event, now);
  const eventStatus = {
    scheduled: "EventScheduled", rescheduled: "EventRescheduled", postponed: "EventPostponed",
    cancelled: "EventCancelled", moved_online: "EventMovedOnline", ended: "EventScheduled",
  }[state];
  const attendanceMode = { in_person: "OfflineEventAttendanceMode", online: "OnlineEventAttendanceMode", hybrid: "MixedEventAttendanceMode" }[s.format];
  const place = s.venue && {
    "@type": "Place", name: s.venue.name,
    address: {
      "@type": "PostalAddress", streetAddress: [s.venue.address_line1, s.venue.address_line2].filter(Boolean).join(", "),
      addressLocality: s.venue.city, addressRegion: s.venue.region, postalCode: s.venue.postal_code, addressCountry: s.venue.country,
    },
    ...(s.venue.lat != null && s.venue.lng != null ? { geo: { "@type": "GeoCoordinates", latitude: s.venue.lat, longitude: s.venue.lng } } : {}),
  };
  const virtual = { "@type": "VirtualLocation", url: pageUrl };
  const location = s.format === "online" ? virtual : s.format === "hybrid" ? [place, virtual].filter(Boolean) : place ?? undefined;

  const availability = effectiveAvailability(s, event, now);
  const offers = availability === "closed" ? undefined : {
    "@type": "Offer", url: pageUrl, price: 0, priceCurrency: "USD",
    availability: `https://schema.org/${{ open: "InStock", limited: "LimitedAvailability", waitlist: "SoldOut", full: "SoldOut", not_open: "PreOrder" }[availability]}`,
    ...(availability === "not_open" && s.rsvp_opens_at ? { validFrom: s.rsvp_opens_at } : {}),
  };

  const organizerNames = [KGOB_ORGANIZER.name, ...(event.co_hosts ?? [])].filter((n, i, a) => n && a.indexOf(n) === i);
  const organizer = (organizerNames || []).map((name) => ({ "@type": "Organization", name, ...(name === KGOB_ORGANIZER.name ? { url: KGOB_ORGANIZER.url } : {}) }));
  const performer = (event.speakers ?? []).map((p) => ({ "@type": "Person", name: p.name, ...(p.title ? { jobTitle: p.title } : {}) }));
  const image = [event.images?.og?.url, event.images?.hero?.url].filter(Boolean);

  return {
    "@context": "https://schema.org", "@type": "Event",
    name: event.title, description: event.summary,
    startDate: isoWithOffset(s.starts_at, s.timezone), endDate: isoWithOffset(s.ends_at, s.timezone),
    eventStatus: `https://schema.org/${eventStatus}`,
    ...(state === "rescheduled" && s.previous_starts_at ? { previousStartDate: isoWithOffset(s.previous_starts_at, s.timezone) } : {}),
    eventAttendanceMode: `https://schema.org/${attendanceMode}`,
    ...(location ? { location } : {}),
    ...(image.length ? { image } : {}),
    organizer,
    ...(performer.length ? { performer } : {}),
    isAccessibleForFree: true,
    ...(offers ? { offers } : {}),
    url: pageUrl,
  };
}

/** JSON-LD for exactly the events and dates shown (K-5). Events without a shown date are skipped. */
export function pageJsonLd(shown, siteOrigin = SITE_ORIGIN, now = new Date()) {
  return (shown || []).filter((x) => !!x.session).map((x) => eventJsonLd(x.event, x.session, siteOrigin, now));
}

/** Serialised for a <script type="application/ld+json">; "<" escaped so it can never close the tag. */
export const serializeJsonLd = (data) => JSON.stringify(data).replace(/</g, "\\u003c");
